import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';
import { HIGHLIGHT_KIND, type Highlight } from '@/lib/highlights';

/** Trash button shown on the user's own highlights; publishes a NIP-09 deletion. */
export function DeleteHighlightButton({ highlight }: { highlight: Highlight }) {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent, isPending } = useNostrPublish();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  if (!user || highlight.event.pubkey !== user.pubkey) {
    return null;
  }

  const confirmDelete = async () => {
    try {
      await createEvent({
        kind: 5,
        content: '',
        tags: [
          ['e', highlight.event.id],
          ['k', String(HIGHLIGHT_KIND)],
        ],
      });

      // Hide immediately, even on relays that haven't honored the deletion.
      queryClient.setQueryData<Set<string>>(['nostr', 'my-deletions', user.pubkey], (old) => {
        const next = new Set(old);
        next.add(highlight.event.id);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'highlights-feed'] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'highlights-author'] });
      queryClient.invalidateQueries({ queryKey: ['nostr', 'highlights-of'] });

      toast({ title: 'Highlight deleted' });
    } catch {
      toast({
        title: 'Failed to delete',
        description: 'Your relays may be unreachable. Try again in a moment.',
        variant: 'destructive',
      });
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
          aria-label="Delete highlight"
        >
          <Trash2 className="size-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this highlight?</AlertDialogTitle>
          <AlertDialogDescription>
            This publishes a deletion request to your relays. Most relays will remove the
            highlight, though relays that don't honor deletions may keep a copy.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmDelete}
            disabled={isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending && <Loader2 className="size-4 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
